import type { CSSProperties, SVGAttributes } from 'react';
import type { ChakraProps, ThemeTypings } from '@chakra-ui/react';
import { chakra, shouldForwardProp, useTheme } from '@chakra-ui/react';

import { appEnv } from '@izimi/core';

import { BookIcon } from './custom-icons/BookIcon';
import { BookOpenIcon } from './custom-icons/BookOpenIcon';
import {
  ArrowDownIcon,
  ArrowUpCircleIcon,
  ArrowUpIcon,
  BellIcon,
  BoxIcon,
  BriefcaseIcon,
  BugIcon,
  BuildingIcon,
  CalendarIcon,
  CertificationIcon,
  CertifiedDocumentIcon,
  CheckIcon,
  CheckStroke,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsUpDownIcon,
  ClearCircleIcon,
  ClearIcon,
  ClockIcon,
  CloudOffIcon,
  CookieIcon,
  CopyIcon,
  CopyStrokeIcon,
  CornerDownRightIcon,
  DocumentIcon,
  DocumentShareIcon,
  DocumentsIcon,
  DocumentSlotIcon,
  DownloadIcon,
  EditIcon,
  ExclamationMarkIcon,
  ExternalLinkSmallIcon,
  EyeIcon,
  EyeStrokeIcon,
  FednotLogoIcon,
  FileStrokeIcon,
  FolderIcon,
  FrownIcon,
  GiveBackIcon,
  GlassesIcon,
  HelpCircleIcon,
  HomeIcon,
  InfoIcon,
  ItsmeIcon,
  LanguageIcon,
  LikeIcon,
  LinkIcon,
  LoginIcon,
  LogoutIcon,
  MailIcon,
  MapPinIcon,
  MinusIcon,
  PenIcon,
  PhoneIcon,
  PlusIcon,
  PushAcceptedIcon,
  PushDeclinedIcon,
  PushIcon,
  RejectIcon,
  RestoreIcon,
  ScanningForVirusIcon,
  Share2Icon,
  ShareIcon,
  SlideOutIcon,
  SSNIcon,
  StampIcon,
  StarIcon,
  TagIcon,
  TalkIcon,
  TextIcon,
  TrashCheckIcon,
  TrashClockIcon,
  TrashIcon,
  UploadIcon,
  UserAddIcon,
  UserArrowIcon,
  UserIcon,
  UserMinusIcon,
  UserOutlineExecutedIcon,
  UserOutlineIcon,
  UserOutlineRejectedIcon,
  UserPlusIcon,
  UserShareAcceptedIcon,
  UserShareDeclinedIcon,
  UserShareIcon,
  UsersIcon,
  VaultCheckIcon,
  VaultCopyIcon,
  VaultIcon,
  VaultSmallIcon,
  VaultTransferIcon,
  WalletIcon,
  WarningIcon,
} from './custom-icons';

interface Props extends Omit<SVGAttributes<SVGElement>, keyof ChakraProps>, ChakraProps {
  name: CustomIconName;
  size?: number;
  color?: ThemeTypings['colors'];
  secondaryColor?: ThemeTypings['colors'];
  ternaryColor?: ThemeTypings['colors'];
  solid?: boolean;
  unfilled?: boolean;
}

const CustomIcon = ({
  name,
  color = 'primary200',
  secondaryColor = 'primary500',
  ternaryColor = 'primary600',
  solid = false,
  ...rest
}: Props) => {
  const theme = useTheme();
  const Icon = customIcons[name];

  if (Icon == null) {
    if (appEnv === 'development' || appEnv === 'local') console.error(`"${name}" icon not found`);
    return null;
  }

  return (
    <Icon
      color={theme.colors[color]}
      secondaryColor={theme.colors[secondaryColor]}
      ternaryColor={theme.colors[ternaryColor]}
      solid={solid}
      {...rest}
    />
  );
};

const chakraOptions = {
  shouldForwardProp: (prop: string) => {
    if (prop === 'color') return true;
    return shouldForwardProp(prop);
  },
};

export default chakra(CustomIcon, chakraOptions);

export type CustomIconProps = Pick<
  Props,
  'size' | 'color' | 'secondaryColor' | 'ternaryColor' | 'solid' | 'unfilled'
> & { style?: CSSProperties };
export type CustomIconName = keyof typeof customIcons;

export const customIcons = {
  'arrow-down': ArrowDownIcon,
  'arrow-up-circle': ArrowUpCircleIcon,
  'arrow-up': ArrowUpIcon,
  box: BoxIcon,
  briefcase: BriefcaseIcon,
  bug: BugIcon,
  building: BuildingIcon,
  certification: CertificationIcon,
  check: CheckIcon,
  'check-stroke': CheckStroke,
  'chevron-down': ChevronDownIcon,
  'chevron-left': ChevronLeftIcon,
  'chevron-right': ChevronRightIcon,
  'chevrons-up-down': ChevronsUpDownIcon,
  clear: ClearIcon,
  'clear-circle': ClearCircleIcon,
  clock: ClockIcon,
  cookie: CookieIcon,
  cornerDownRight: CornerDownRightIcon,
  copy: CopyIcon,
  'copy-stroke': CopyStrokeIcon,
  'document-share': DocumentShareIcon,
  'document-slot': DocumentSlotIcon,
  document: DocumentIcon,
  documents: DocumentsIcon,
  download: DownloadIcon,
  upload: UploadIcon,
  'exclamation-mark': ExclamationMarkIcon,
  eye: EyeIcon,
  'eye-stroke': EyeStrokeIcon,
  fednot: FednotLogoIcon,
  frown: FrownIcon,
  'give-back': GiveBackIcon,
  glasses: GlassesIcon,
  'help-circle': HelpCircleIcon,
  home: HomeIcon,
  info: InfoIcon,
  itsme: ItsmeIcon,
  language: LanguageIcon,
  like: LikeIcon,
  link: LinkIcon,
  login: LoginIcon,
  logout: LogoutIcon,
  mail: MailIcon,
  'map-pin': MapPinIcon,
  pen: PenIcon,
  phone: PhoneIcon,
  'push-accepted': PushAcceptedIcon,
  'push-declined': PushDeclinedIcon,
  push: PushIcon,
  star: StarIcon,
  restore: RestoreIcon,
  'scanning-for-virus': ScanningForVirusIcon,
  'share-2': Share2Icon,
  share: ShareIcon,
  'slide-out': SlideOutIcon,
  SSN: SSNIcon,
  stamp: StampIcon,
  tag: TagIcon,
  talk: TalkIcon,
  text: TextIcon,
  trash: TrashIcon,
  'user-add': UserAddIcon,
  'user-arrow': UserArrowIcon,
  'user-minus': UserMinusIcon,
  'user-outline-executed': UserOutlineExecutedIcon,
  'user-outline': UserOutlineIcon,
  'user-outline-rejected': UserOutlineRejectedIcon,
  'user-plus': UserPlusIcon,
  'user-share': UserShareIcon,
  'user-share-accepted': UserShareAcceptedIcon,
  'user-share-declined': UserShareDeclinedIcon,
  user: UserIcon,
  users: UsersIcon,
  'vault-transfer': VaultTransferIcon,
  vault: VaultIcon,
  'vault-check': VaultCheckIcon,
  'vault-copy': VaultCopyIcon,
  'certified-document': CertifiedDocumentIcon,
  'cloud-off': CloudOffIcon,
  'trash-check': TrashCheckIcon,
  'trash-clock': TrashClockIcon,
  plus: PlusIcon,
  book: BookIcon,
  'book-open': BookOpenIcon,
  calendar: CalendarIcon,
  reject: RejectIcon,
  edit: EditIcon,
  minus: MinusIcon,
  'external-link-small': ExternalLinkSmallIcon,
  bell: BellIcon,
  'file-stroke': FileStrokeIcon,
  folder: FolderIcon,
  'vault-small': VaultSmallIcon,
  warning: WarningIcon,
  wallet: WalletIcon,
};
